/**
 * The Artifacts gallery store — loads ONE companion's finished `media_assets` and
 * drives the shared `Lightbox` (swipe/next/prev, set-as-avatar). The header
 * Artifacts button and an inline gallery click both funnel here, opening the SAME
 * viewer over her full set (the inline click just lands on the clicked asset).
 * Thin: the edge is the authority on the list + its signed URLs; this holds the
 * open state + cursor and the pure index math from `lightbox-model`.
 */
import { type ApiError, isErr, type Result } from '@4f/shared';
import { createSignal } from 'solid-js';
import type { ApiClient } from '../api/client.ts';
import type { CompanionView, MediaAssetView } from '../api/types.ts';
import {
  clampIndex,
  nextIndex,
  prevIndex,
  startIndexForAsset,
} from '../features/gallery/lightbox-model.ts';

/** The outcome of an open request — lets the shell toast "no media yet" without peeking at state. */
export interface GalleryOpenResult {
  /** True when the viewer opened (≥1 asset); false on an empty set or a load error. */
  readonly opened: boolean;
  readonly count: number;
}

export interface GalleryStore {
  readonly items: () => readonly MediaAssetView[];
  readonly index: () => number;
  /** The asset currently on display, or `null` when closed / empty. */
  readonly current: () => MediaAssetView | null;
  readonly isOpen: () => boolean;
  readonly loading: () => boolean;
  readonly savingAvatar: () => boolean;
  readonly error: () => ApiError | null;
  /** Load her gallery and open the viewer at `startAssetId` (or the start). */
  openFor(girlfriendId: string, startAssetId?: string): Promise<GalleryOpenResult>;
  next(): void;
  prev(): void;
  close(): void;
  /** Promote the current asset to her avatar; the shell reloads the switcher on `ok`. */
  setCurrentAsAvatar(): Promise<Result<CompanionView, ApiError> | null>;
}

export function createGalleryStore(api: ApiClient): GalleryStore {
  const [items, setItems] = createSignal<readonly MediaAssetView[]>([]);
  const [index, setIndex] = createSignal(0);
  const [isOpen, setOpen] = createSignal(false);
  const [loading, setLoading] = createSignal(false);
  const [savingAvatar, setSavingAvatar] = createSignal(false);
  const [error, setError] = createSignal<ApiError | null>(null);

  let girlfriendId: string | null = null;

  // A plain derivation (not a memo): reads the current signals on every call, so
  // it stays correct whether read in a reactive render or a bare test. Consumers
  // that need reactivity (the `ArtifactsGallery` component) wrap it in their own
  // memo/effect, which tracks `items`/`index` through this read.
  const current = (): MediaAssetView | null => {
    const list = items();
    if (list.length === 0) {
      return null;
    }
    return list[clampIndex(index(), list.length)] ?? null;
  };

  const openFor = async (gfId: string, startAssetId?: string): Promise<GalleryOpenResult> => {
    girlfriendId = gfId;
    setLoading(true);
    setError(null);
    try {
      const result = await api.listGallery(gfId);
      if (isErr(result)) {
        setError(result.error);
        setOpen(false);
        return { opened: false, count: 0 };
      }
      const assets = result.value;
      setItems(assets);
      setIndex(startIndexForAsset(assets, startAssetId));
      const opened = assets.length > 0;
      setOpen(opened);
      return { opened, count: assets.length };
    } finally {
      setLoading(false);
    }
  };

  const next = (): void => {
    setIndex((i) => nextIndex(i, items().length));
  };
  const prev = (): void => {
    setIndex((i) => prevIndex(i, items().length));
  };
  const close = (): void => {
    setOpen(false);
  };

  const setCurrentAsAvatar = async (): Promise<Result<CompanionView, ApiError> | null> => {
    const asset = current();
    if (!asset || !girlfriendId) {
      return null;
    }
    setSavingAvatar(true);
    try {
      return await api.setAvatar(girlfriendId, asset.assetId);
    } finally {
      setSavingAvatar(false);
    }
  };

  return {
    items,
    index,
    current,
    isOpen,
    loading,
    savingAvatar,
    error,
    openFor,
    next,
    prev,
    close,
    setCurrentAsAvatar,
  };
}
